import {
  each,
  merge,
  isString,
  isObject,
  isUndefined,
  queryString,
  labelReplace,
  labelReplaceExp
} from '@x-drive/utils'
import { Toast } from '@zz-common/zz-ui'
import nativeLogin from '@utils/newLogin'
import { getRouteTag } from '@utils'
import { SentryCapture } from '@zz-common/sentry'
/**环境变量 */
const ENV: object = process.env

/**所有 api 存储对象 */
const APIS: object = {}

/**所有预设域名存储对象 */
const HOSTS: object = {}

/**请求成功状态码 */
const CODE_SUCCESS: number = 0

/*请求登陆状态码*/
const CODE_LOGIN: any = '-2'
const CODE_LOGIN_F: any = '-400'
/**环境变量中请求域名判断正则 */
const API_HOST_REG_RXP: RegExp = /_API_(.*)_HOST$/

/**请求方法判断正则 */
const REQ_METHOD_REQ_EXP: RegExp = /get|head|delete/

/**
 * 网络协议检测正则
 */
const PROTOCOL_REG_EXP: RegExp = /^http[s]?:/i

/**接口名称声明判断 */
const API_REQ_PATH_REG_EXP: RegExp = /^\//i

const URL_PREFIX = getRouteTag()

each(ENV, (val: string, key: string) => {
  if (API_HOST_REG_RXP.test(key)) {
    let name: string = key.match(API_HOST_REG_RXP)[1]
    name = name.toLocaleLowerCase()
    HOSTS[name] = val
  }
})

/**字段映射对象 */
interface DataKeysMap {
  /**业务数据字段 */
  data?: string

  /**业务状态码字段 */
  code?: string

  /**业务返回信息字段 */
  message?: string

  /**次要业务返回信息字段 */
  secMsg?: string
}

/**
 * 请求配置
 */
interface ReqConf {
  /**是否自动提示接口返回的信息 */
  autoToast?: boolean

  /**请求数据类型 */
  dataType?: string

  /**是否添加随机数 */
  fresh?: boolean

  /**是否携带 cookie */
  credentials?: boolean

  /**请求头设置 */
  header?: object

  /**超时时间 */
  timeout?: number

  /**是否返回原始数据 */
  raw?: boolean

  /**字段映射对象 */
  keys?: DataKeysMap

  /**请求成功状态码 */
  successCode?: number

  /*请求登陆状态码 -2 */
  loginCode?: number | string
  //请求状态码-400
  loginCodeFour?: number | string
}

/**
 * 请求参数 (query)
 */
interface ReqParams {
  [propName: string]: any
}

/**
 * 请求数据 (post)
 */
interface ReqData {
  [propName: string]: any
}

/**
 * 请求 promise 对象
 */
interface ReqPromise extends Promise<any> {
  [propName: string]: any
}

/**
 * 各个页面包含的请求
 */
const RequestQueue: object = {}

/**
 * 加入到列表
 * @param  {Object} key 当前的页面对象
 * @param  {Object} val 请求对象
 */
const pushQueue = function(val: XMLHttpRequest) {
  const pathname = window.location.pathname
  if (!RequestQueue[pathname]) {
    RequestQueue[pathname] = []
  }
  RequestQueue[pathname].push(val)
}

/**
 * 从列表删除一个请求
 * @param  {Object} key 当前页面对象
 * @param  {Object} val 请求对象
 */
const spliceQueue = function(val: XMLHttpRequest) {
  const pathname = window.location.pathname
  if (RequestQueue[pathname] && RequestQueue[pathname].length) {
    const index = RequestQueue[pathname].indexOf(val)
    if (index !== -1) {
      RequestQueue[pathname].splice(index, 1)
    }
  }
}

/**
 * 解析生成正确的数据请求地址
 * @param  {String} url    接口别名或具体的请求地址
 * @param  {Object} params 请求参数对象
 * @return {String}
 */
export function resloveUrl(uri: string, params?: object): string {
  let oUri = uri
  uri = APIS[uri]
  uri = uri || oUri
  oUri = null
  if (isObject(params)) {
    uri = uri.replace(labelReplaceExp, function(_, key) {
      const re = params[key]
      delete params[key]
      return re
    })
  }
  return uri
}

class Request {
  constructor() {
    console.log('constructor')
  }

  /**默认请求配置 */
  private defConf: ReqConf = {
    autoToast: true,
    dataType: 'json',
    fresh: true,
    credentials: true,
    successCode: CODE_SUCCESS,
    loginCode: CODE_LOGIN,
    loginCodeFour: CODE_LOGIN_F,
    header: {
      'X-Requested-With': 'XMLHttpRequest'
    },
    timeout: 10000,
    raw: false
  }

  /**检测是否同域用的 a 标签 */
  static A: any = document.createElement('a')

  /**字段映射对象 */
  keys: DataKeysMap = {
    data: 'respData',
    code: 'respCode',
    message: 'errorMsg',
    secMsg: 'errMsg'
  }

  /**
   * 注册一个请求 api 模块
   * @static
   * @param  subject  模块 api 设置
   * @param  host     api 请求域名
   */
  static register(subject: object, host?: string): void {
    if (isObject(subject)) {
      each(subject, (val, key) => {
        if (APIS[key]) {
          console.warn(`API ${key} 已被定义`)
        } else {
          if (!API_REQ_PATH_REG_EXP.test(key)) {
            key = `/${key}`
          }
          if (isString(host) && !PROTOCOL_REG_EXP.test(val)) {
            if (!API_REQ_PATH_REG_EXP.test(val)) {
              val = `/${val}`
            }
            val = `${host}${val}`
          } else if (!PROTOCOL_REG_EXP.test(val)) {
            // 如果接口不以 http 或 https 开头的，则尝试替换里面的占位符
            // 这里只替换可能存在的 host，所以必须保留其他任何未能解析的花括号
            val = labelReplace(val, HOSTS, true)
          }
          APIS[key] = val
        }
      })
    }
  }

  /**
   * 放弃当前正在发起的所有请求
   */
  static cancel() {
    const pathname = window.location.pathname
    const nowReqs = RequestQueue[pathname]
    if (nowReqs && nowReqs.length) {
      try {
        for (const req of nowReqs) {
          req.abort()
        }
      } catch (e) {
        console.error(e)
      }
    }
    RequestQueue[pathname] = []
  }

  /**
   * 生成一个 16 进制的随机数
   */
  static randomStr(): string {
    return (Date.now() + Math.random()).toString(16)
  }

  /**
   * 解析生成正确的数据请求地址
   * @param {String} url    接口别名或具体的请求地址
   * @param {Object} params 请求参数对象
   * @return {String}
   */
  resolveUri(uri: string, params: object) {
    return resloveUrl(uri, params)
  }

  /**
   * 处理 post 数据
   * @param data post 数据
   * @return 处理过后的数据
   */
  private parseData(data: string | object): string {
    if (isString(data)) {
      return (data as string).replace(/\+/g, '%2B')
    }
    return JSON.stringify(data)
  }

  /**
   * 将对象转化为 form data
   * @param data 要转化成 form data 的数据
   * @return form data
   */
  private parseFormData(data: object): FormData {
    const fd = new FormData()
    if (isObject(data)) {
      each(data, (val: any, key: string) => {
        fd.append(key, val)
      })
    }
    return fd
  }

  /**
   * 判断是否处于同一个域名下
   * @param url 待判断的地址
   */
  private checkOriginHost(url: string): boolean {
    Request.A.href = url
    return Request.A.host === window.location.host
  }

  /**
   * 执行请求
   * @param   {String}  type    请求类型
   * @param   {String}  url     请求url或别名
   * @param   {Object}  param   请求参数
   * @param   {Object}  data    请求数据
   * @param   {ReqConf} config  请求配置
   * @returns {Object}          请求 Promise 对象
   */
  run<T>(
    type: string,
    url: string,
    params: ReqParams = {},
    data: ReqData = {},
    config: ReqConf = {}
  ): Promise<T> {
    type = type.toLocaleLowerCase()

    const reqConf: ReqConf = merge({}, this.defConf, config)

    if (reqConf.fresh) {
      // 有强制刷新设置则自动追加随机数
      params._ = Request.randomStr()
    }

    const originUrl = url

    // 解析地址
    url = this.resolveUri(url, params)
    // 处理 query 参数
    if (isObject(params)) {
      url += `?${queryString(params)}`
    }
    if (URL_PREFIX) {
      const urlSplit = url.split('.zhuanzhuan.com')
      if (urlSplit) {
        url = urlSplit[0] + URL_PREFIX + '.zhuanzhuan.com' + urlSplit[1]
      }
    }

    const dataKeys = config.keys || this.keys

    const xhr = new XMLHttpRequest()
    const req: ReqPromise = new Promise((resolve: Function, reject: Function) => {
      let reqData: string | FormData
      const header: object = reqConf.header
      const isCrossOrigin = !this.checkOriginHost(url)

      if (!REQ_METHOD_REQ_EXP.test(type)) {
        if (!header['Content-Type']) {
          header['Content-Type'] = 'application/json'
        }
        if (header['Content-Type'] === null) {
          // formdata
          delete header['Content-Type']
          reqData = this.parseFormData(data)
        } else {
          // 默认json
          reqData = this.parseData(data)
        }
      }

      if (isCrossOrigin) {
        if (reqConf.credentials) {
          xhr.withCredentials = true
        }
        delete header['X-Requested-With']
      }

      if (config.timeout) {
        xhr.timeout = config.timeout
      }

      xhr.open(type, url, true)

      each(header, (val, key) => {
        xhr.setRequestHeader(key, val)
      })

      xhr.onload = function() {
        spliceQueue(this)
        if ((this.status >= 200 && this.status < 300) || this.status === 304) {
          let re: any = this.responseText
          if (reqConf.dataType === 'json') {
            try {
              re = JSON.parse(re)
            } catch (err) {
              reject(err)
              return
            }
          }

          const data = re[dataKeys.data]
          const code = re[dataKeys.code]
          if (
            Number(code) === reqConf.successCode ||
            (originUrl === '/getRecycle' && +re.code === 1)
          ) {
            return resolve(config.raw ? re : data || re.data || {})
          }
          if (code == reqConf.loginCode || code == reqConf.loginCodeFour) {
            console.log(code, 'code111')
            nativeLogin(null, null, true)
            reject(re)
            return false
          }
          const message: string = re[dataKeys.message] || re[dataKeys.secMsg] || ''
          if (reqConf.autoToast && message) {
            Toast({
              message
            })
          }
          SentryCapture(new Error(`Request failed, [${originUrl}], code [${code}]`), 3, {
            extra: re
          })

          return reject(re)
        } else {
          const Err = new Error(`Request Error, [${originUrl}], status [${this.status}]`)
          SentryCapture(Err, 3, { extra: { url, params, data } })
          reject(Err)
        }
      }

      xhr.ontimeout = xhr.onerror = function(e) {
        spliceQueue(this)
        reject(e)
      }

      xhr.send(reqData)
    })

    req.abort = function() {
      xhr.abort()
      spliceQueue(xhr)
    }
    pushQueue(xhr)
    return req
  }

  /**
   * 发起一个 get 请求
   * @param   {String}   url     请求url或别名
   * @param   {Object}   param   请求参数
   * @param   {ReqConf}  config  请求配置
   * @returns {Object}
   */
  get<T>(url: string, param?: object, config?: ReqConf) {
    return this.run<T>('GET', url, param, {}, config)
  }

  /**
   * 发起一个 post 请求
   * @param   {String}  url      请求url或别名
   * @param   {Object}  param    请求参数
   * @param   {Object}  data     请求数据
   * @param   {ReqConf} config  请求配置
   * @returns {Object}
   */
  post<T>(url: string, param?: object, data?: object, config?: ReqConf) {
    if (isObject(param) && isUndefined(data)) {
      data = param
      param = {}
    }
    if (isUndefined(data)) {
      data = {}
    }
    return this.run<T>('POST', url, param, data, config)
  }
}

export { Request as R }

export default new Request()
